import { Component, Inject, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { TranslateService } from '@ngx-translate/core';

import { CustomerFacade, LoginFacade, ModalRef, ModalService, NotificationFacade } from '@ridango/components';
import { AppStartupService } from '@ridango/core';
import { AccountDeleteConfirmationType } from '@ridango/core/lib/enums/features.enum';

import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'rwidget-customer-account-detail',
    templateUrl: './customer-account-detail.component.html',
    styleUrls: ['./customer-account-detail.component.scss']
})
export class CustomerAccountDetailComponent implements OnInit, OnDestroy {
    private deleteConfirmationModal: ModalRef;
    private componentDestroyed$ = new Subject<void>();

    @ViewChild('modal') modalEl; // to focus on opened modal
    @ViewChild('actionSelector') actionSelectorEl; // to focus when modal is closed
    @ViewChild('deleteConfirmation', { read: TemplateRef, static: true }) deleteConfirmationRef: TemplateRef<any>;

    profile$: Observable<any> = this.customerFacade.profile$;
    actions$: Observable<string[]> = this.customerFacade.profileActions$;
    customerFields$: Observable<string[]> = this.customerFacade.customerFields$;

    isDeletionSuccess$: Observable<boolean> = this.customerFacade.isDeletionSuccess$;
    error$: Observable<any> = this.customerFacade.error$;

    isLoading = false;
    locale = 'en';
    actionsDropdownOpened = false;
    contactPerson: string;
    contactPhone: string;

    accountDeleteConfirmationType = AccountDeleteConfirmationType;
    accountDeletionConfirmed = false;

    constructor(
        @Inject('ENV') public env: any,
        private customerFacade: CustomerFacade,
        private modalService: ModalService,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private translate: TranslateService,
        private notification: NotificationFacade,
        private loginFacade: LoginFacade,
        public appStartupService: AppStartupService
    ) {}

    ngOnInit() {
        this.customerFacade.getCustomerProfile();
        this.error$.pipe(takeUntil(this.componentDestroyed$)).subscribe((error) => {
            if (error) {
                this.deleteConfirmationModal.close();
                this.isLoading = false;
                this.handleError(error);
            }
        });
        this.isDeletionSuccess$.pipe(takeUntil(this.componentDestroyed$)).subscribe((success) => {
            success && this.handleForget();
        });
    }

    ngOnDestroy() {
        this.componentDestroyed$.next();
        this.componentDestroyed$.complete();
    }

    dropdownStateChange(isOpened: boolean) {
        this.actionsDropdownOpened = isOpened;
    }

    actionClicked(action: string) {
        switch (action) {
            case 'ACCOUNT_ACTION_EDIT':
                this.navigateRelative(['edit']);
                break;
            case 'ACCOUNT_ACTION_CHANGE_PASSWORD':
                this.navigateRelative(['change-password']);
                break;
            case 'ACCOUNT_ACTION_CHANGE_EMAIL':
                this.navigateRelative(['change-email']);
                break;
            case 'ACCOUNT_ACTION_DELETE_ACCOUNT':
                this.deleteConfirmationModal = this.modalService.open(this.deleteConfirmationRef);
                setTimeout(() => this.modalEl.firstFocus.nativeElement.focus());
                break;
        }
    }

    resetPeriod(period: string) {
        switch (period) {
            case 'N':
                return 'ENTITLEMENTS_INFINITY';
            case 'Q':
                return 'ENTITLEMENTS_QUARTERLY';
            case 'M':
                return 'ENTITLEMENTS_MONTHLY';
            case 'W':
                return 'ENTITLEMENTS_WEEKLY';
            case 'D':
                return 'ENTITLEMENTS_DAILY';
        }
    }

    getContactPerson(profile) {
        if (profile.customer_properties) {
            this.contactPerson = this.findProperty(profile, 'ONDEMAND_CUSTOMER_REQUEST_INFO').value;
            return this.contactPerson;
        }
    }

    findProperty(profile, code: string) {
        const property = profile.customer_properties.find((object) => object.code === code);
        if (property) {
            return property;
        } else {
            return 0;
        }
    }

    navigateRelative(to: any[]) {
        this.router.navigate(to, { relativeTo: this.activatedRoute });
    }

    cancelDelete() {
        this.deleteConfirmationModal.close();
        this.actionSelectorEl.menuButton.nativeElement.focus();
    }

    confirmDelete() {
        this.isLoading = true;
        this.customerFacade.sendCustomerProfileDeletionEmail();
    }

    toggleDeleteConfirmCheckbox() {
        this.accountDeletionConfirmed = !this.accountDeletionConfirmed;
    }

    confirmDeleteWithCheckbox() {
        if (this.accountDeletionConfirmed) {
            this.isLoading = true;
            this.customerFacade.deleteProfile();
            this.handleForget();
        }
    }

    handleForget() {
        this.isLoading = false;
        this.deleteConfirmationModal.close();

        this.loginFacade.logout();
        this.router.navigate([this.translate.currentLang, 'home']);
        this.customerFacade.clearCustomerProfile();
        this.notification.success(this.translate.instant('LOCAL_CUSTOMER_FORGET_SUCCESS'), {
            title: this.translate.instant('LOCAL_CUSTOMER_FORGET_SUCCESS_TITLE'),
            showAfterRouteChange: false
        });
    }

    handleError(error) {
        this.notification.error(this.translate.instant(error.error));
    }

    getPhoneNumber(profile): string | undefined {
        if (profile.customer_properties) {
            this.contactPhone = this.findProperty(profile, 'CONTACT_PHONE').value;
            return this.contactPhone;
        }
    }
}
